import { useCallback, useEffect, useRef, useState } from 'react';

import { getBriefPdfTitle } from 'features/briefs/briefPdfUtility';

import { Brief } from 'shared/typings/briefs';

interface BriefPdfPrint {
  /** True while the printable brief document should be mounted. */
  isPrinting: boolean;
  exportPdf: () => void;
}

/**
 * Drives the brief PDF export: mounts the printable document, opens the browser's
 * print dialog once it is in the DOM, and cleans up when the dialog closes.
 */
export const useBriefPdfPrint = (brief: Brief): BriefPdfPrint => {
  const [isPrinting, setIsPrinting] = useState(false);
  const previousTitle = useRef<string | null>(null);

  const restoreTitle = useCallback(() => {
    if (previousTitle.current !== null) {
      document.title = previousTitle.current;
      previousTitle.current = null;
    }
  }, []);

  const exportPdf = useCallback(() => {
    previousTitle.current = document.title;
    document.title = getBriefPdfTitle(brief);
    setIsPrinting(true);
  }, [brief]);

  // Runs after the printable document has been committed to the DOM, so the
  // print dialog always previews the full brief.
  useEffect(() => {
    if (isPrinting) {
      window.print();
    }
  }, [isPrinting]);

  useEffect(() => {
    const onAfterPrint = () => {
      setIsPrinting(false);
      restoreTitle();
    };
    window.addEventListener('afterprint', onAfterPrint);

    return () => {
      window.removeEventListener('afterprint', onAfterPrint);
      restoreTitle();
    };
  }, [restoreTitle]);

  return { isPrinting, exportPdf };
};
